import { useEffect, useState } from 'preact/hooks';
import { type ConformanceRollup, DashboardApi, HttpError } from '../api.js';
import type { AuthOrg } from '../auth.js';
import { Banner } from '../components/Banner.js';

const api = new DashboardApi();

/** Score → colour, matching the public badge bands (grey when no data). */
const scoreColor = (score: number | null): string =>
  score === null
    ? '#868e96'
    : score >= 95
      ? '#2f9e44'
      : score >= 80
        ? '#66a80f'
        : score >= 60
          ? '#f59f00'
          : '#e03131';

function Stat({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div class="card" style={{ minWidth: 130, padding: '12px 16px' }}>
      <div class="small muted" style={{ textTransform: 'uppercase', letterSpacing: '.06em' }}>
        {label}
      </div>
      <div style={{ fontSize: '1.7rem', fontWeight: 700, ...(color ? { color } : {}) }}>{value}</div>
    </div>
  );
}

/**
 * Portfolio conformance — every product's latest-scan accessibility conformance
 * in one worst-first view, with an org-level summary. Reads the session
 * conformance rollup (GET /v1/orgs/:orgId/conformance).
 */
export function Portfolio({ org }: { org: AuthOrg }) {
  const [data, setData] = useState<ConformanceRollup | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let live = true;
    setLoading(true);
    api
      .getConformanceRollup(org.id)
      .then((r) => {
        if (live) {
          setData(r);
          setErr(null);
        }
      })
      .catch((e) => {
        if (live) setErr(e instanceof HttpError ? e.message : String(e));
      })
      .finally(() => {
        if (live) setLoading(false);
      });
    return () => {
      live = false;
    };
  }, [org.id]);

  const s = data?.summary;
  return (
    <div>
      <h1 style={{ margin: '0 0 4px' }}>Portfolio conformance</h1>
      <p class="muted small">
        Every product's latest-scan accessibility conformance, worst-first — a supports-weighted
        score across evaluated WCAG criteria.
      </p>

      {err && <Banner variant="warn">{err}</Banner>}
      {loading && <p class="muted">Loading…</p>}

      {s && (
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', margin: '16px 0' }}>
          <Stat label="Products" value={String(s.productCount)} />
          <Stat label="Scanned" value={`${s.scannedCount} / ${s.productCount}`} />
          <Stat
            label="Avg score"
            value={s.avgScore === null ? '—' : `${s.avgScore}%`}
            color={scoreColor(s.avgScore)}
          />
          <Stat
            label="Worst score"
            value={s.worstScore === null ? '—' : `${s.worstScore}%`}
            color={scoreColor(s.worstScore)}
          />
          <Stat
            label="Total failures"
            value={String(s.totalDoesNotSupport)}
            color={s.totalDoesNotSupport > 0 ? '#e03131' : '#2f9e44'}
          />
        </div>
      )}

      {data && data.products.length > 0 && (
        <div class="card">
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
            <thead>
              <tr class="muted small" style={{ textAlign: 'left' }}>
                <th style={{ padding: '6px 8px' }}>Product</th>
                <th style={{ padding: '6px 8px' }}>Score</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Supports</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Partial</th>
                <th style={{ padding: '6px 8px', textAlign: 'right' }}>Fails</th>
                <th style={{ padding: '6px 8px' }}>Last scan</th>
              </tr>
            </thead>
            <tbody>
              {data.products.map((p) => (
                <tr key={p.slug} style={{ borderTop: '1px solid var(--border, #2a3140)' }}>
                  <td style={{ padding: '7px 8px' }}>{p.name}</td>
                  <td style={{ padding: '7px 8px' }}>
                    {p.scanned && p.score !== null ? (
                      <strong style={{ color: scoreColor(p.score) }}>{p.score}%</strong>
                    ) : (
                      <span class="muted small">not scanned</span>
                    )}
                  </td>
                  <td style={{ padding: '7px 8px', textAlign: 'right' }}>{p.supports ?? '—'}</td>
                  <td style={{ padding: '7px 8px', textAlign: 'right' }}>{p.partial ?? '—'}</td>
                  <td
                    style={{
                      padding: '7px 8px',
                      textAlign: 'right',
                      color: (p.doesNot ?? 0) > 0 ? '#e03131' : undefined,
                    }}
                  >
                    {p.doesNot ?? '—'}
                  </td>
                  <td class="muted small" style={{ padding: '7px 8px' }}>
                    {p.ts ? new Date(p.ts).toLocaleDateString() : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.products.length === 0 && !loading && (
        <p class="muted">No products yet — add one under Products to see its conformance here.</p>
      )}
      {data?.truncated && (
        <p class="muted small">
          Showing the first {data.summary.returned} of {data.summary.productCount} products.
        </p>
      )}
    </div>
  );
}
